// lib, util, constant
import { useState, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import McpServerApi from '@/common/constant/api/McpServerApi';
import localStorageUtil from '@/common/utils/localStorageUtil';
import CorporationUtils from '@/common/utils/CorporationUtils';
import { parseLocalMcpScope } from '@/common/utils/mcpUtils';
import { LOCAL_MCP_PROJECT_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';

// service
import { getListMCPServer, filterListMCPInProject } from './handler/MCPServerLogicHandler';
import { getLocalMcpListForOrg, getLocalMcpListForProject } from '@/service/mcpServerService';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { userAtom } from '@/state/atom/userAtom';
import { mcpActivationChangedAtom } from '@/state/atom/panelAtom';

// component
import MCPServerListInProject from './components/MCPServerListInProject';
import ProjectLocalMcpTab from './components/ProjectLocalMcpTab';
import LoadingPanel from '@/component/common/LoadingPanel';
import EditMCPServer from './components/EditMCPServer';
import MCPServerList from './components/MCPServerList';

// css
import './MCPServerSetting.css';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

MCPServerSetting.propTypes = {
    panelType: PropTypes.string
};

function MCPServerSetting(props) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const { panelType } = props;

    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const userDetail = useRecoilValue(userAtom);
    const mcpActivationChanged = useRecoilValue(mcpActivationChangedAtom);

    const [isLoading, setIsLoading] = useState(false);
    const [listSettingApi, setListSettingApi] = useState([]);
    const [listSettingApiProject, setListSettingApiProject] = useState([]);
    const [localMcpList, setLocalMcpList] = useState([]);
    const [projectLocalMcpList, setProjectLocalMcpList] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    let oauth2Data = Utils.convertJSONDataToObject(localStorageUtil.getOauth2LastAuthen()) || {};

    const [editApiData, setEditApiData] = useState(oauth2Data);

    const fetchServerMCPList = () => {
        return getListMCPServer(
            panelType,
            corporationDetail.id,
            projectInuseDetail.id,
            userDetail.id,
            McpServerApi.MCP_SERVER_GET_METHOD.GET_LIST_PAGINATION
        )
            .then(res => {
                const listApi = res?.listMCPServer || [];
                if (!Utils.isEmptyArray(listApi)) {
                    setListSettingApi(listApi);
                    let listSettingOfProject = filterListMCPInProject(listApi);
                    listSettingOfProject = listSettingOfProject.map(i => ({
                        ...i,
                        authMethod: Utils.transformAuthMethod(i?.authMethod),
                    }));
                    setListSettingApiProject(listSettingOfProject);
                }
            })
            .catch(error => {
                console.log('getListMCPServer error >>>', error);
            });
    };

    const fetchLocalMcpList = () => {
        if (panelType !== Constant.PANEL_TYPE.PROJECT_MANAGEMENT) {
            return getLocalMcpListForOrg(corporationDetail.id).then(list => {
                setLocalMcpList(list);
            });
        }
        return getLocalMcpListForProject(corporationDetail.id, projectInuseDetail.id, userDetail.id).then(list => {
            const userItems = [];
            const projectItems = [];
            list.forEach(item => {
                const { prefix } = parseLocalMcpScope(item.catId);
                if (prefix === LOCAL_MCP_PROJECT_PREFIX) {
                    projectItems.push(item);
                } else {
                    userItems.push(item);
                }
            });
            setLocalMcpList(userItems);
            setProjectLocalMcpList(projectItems);
        });
    };

    const fetchAllData = () => {
        setIsLoading(true);
        Promise.all([fetchServerMCPList(), fetchLocalMcpList()])
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchAllData();
        return () => {
            setListSettingApi([]);
            setListSettingApiProject([]);
            setLocalMcpList([]);
            setProjectLocalMcpList([]);
            setIsLoading(false);
        };
    }, []);

    const handleBackWithReload = () => {
        setEditApiData({});
        setListSettingApi([]);
        fetchAllData();
    };

    // BR-8: Tab「プロジェクト用ローカルMCP」show when:
    // Not displayed on mobile/tablet devices
    const isMobileOrTablet = useMemo(() => Utils.isMobileOrTabletDevice(), []);

    // Org MCP = ON + project has at least 1 [P] Local MCP item active
    const isDisplayProjectLocalMcpTab = useMemo(() => {
        if (isMobileOrTablet) return false;
        if (panelType !== Constant.PANEL_TYPE.PROJECT_MANAGEMENT) return false;
        if (!corporationDetail || !projectInuseDetail || !userDetail) return false;
        
        // check ON/OFF service
        const isEnableMCPService = CorporationUtils.isCanUsingMCPService(corporationDetail, projectInuseDetail);
        
        // check is some ON mcp in prj — prefer atom value (updated on toggle) over stale projectSetting
        const prjOfProjectLocalMCP = {
            ...(Utils.convertJSONDataToObject(projectInuseDetail?.projectSetting)?.projectLocalMcpActivated || {}),
            ...(mcpActivationChanged?.projectLocalMcp || {})
        };

        if (Utils.isEmptyObject(prjOfProjectLocalMCP)) return false;

        const isEnableLocalMcpItem = Object.values(prjOfProjectLocalMCP).some(v => v === true);
        if (!isEnableLocalMcpItem) return false;

        return isEnableMCPService;
    }, [isMobileOrTablet, panelType, corporationDetail?.id, projectInuseDetail?.id, userDetail?.id, mcpActivationChanged?.projectLocalMcp]);

    const changeTabOnProjectManagement = (redirectTab) => {
        if (redirectTab === activeTab) return;
        if (redirectTab === 1 && !isDisplayProjectLocalMcpTab) return;

        setActiveTab(redirectTab);
        if (activeTab !== 0 && redirectTab === 0) {
            fetchAllData();
        }
    };

    // ufe redirect tab on project management panel
    useEffect(() => {
        if (panelType !== Constant.PANEL_TYPE.PROJECT_MANAGEMENT) return;
        if (!isDisplayProjectLocalMcpTab && activeTab === 1) {
            // handle back to first tab: MCP list
            const redirectTab = 0;
            changeTabOnProjectManagement(redirectTab)
        }
    }, [isDisplayProjectLocalMcpTab, changeTabOnProjectManagement]);

    const renderContent = () => {
        if (Utils.isEmptyObject(editApiData)) {
            if (panelType === Constant.PANEL_TYPE.PROJECT_MANAGEMENT) {
                return (
                    <div style={{ height: '100%' }}>
                        {/* Only display tabs control when tabs.length > 1 */}
                        {isDisplayProjectLocalMcpTab && (
                            <div className="mcp-server-tabs">
                                <button
                                    className={`mcp-server-tab-btn ${activeTab === 0 ? 'active' : ''}`}
                                    onClick={() => changeTabOnProjectManagement(0)}
                                >
                                    {trI18n('MCP_SERVER_SETTING_TAB_LIST')}
                                </button>
                                <button
                                    className={`mcp-server-tab-btn ${activeTab === 1 ? 'active' : ''}`}
                                    onClick={() => changeTabOnProjectManagement(1)}
                                >
                                    {trI18n('MCP_SERVER_SETTING_TAB_PROJECT_LOCAL_MCP')}
                                </button>
                            </div>
                        )}
                        {activeTab === 0 && (
                            <MCPServerListInProject
                                setIsLoading={setIsLoading}
                                panelType={panelType}
                                setEditApiData={setEditApiData}
                                corporationDetail={corporationDetail}
                                projectInuseDetail={projectInuseDetail}
                                userDetail={userDetail}
                                listSettingApiProject={listSettingApiProject}
                                setListSettingApiProject={setListSettingApiProject}
                                props={props}
                                localMcpList={localMcpList}
                                setLocalMcpList={setLocalMcpList}
                                projectLocalMcpList={projectLocalMcpList}
                                setProjectLocalMcpList={setProjectLocalMcpList}
                            />
                        )}
                        {activeTab === 1 && (
                            <ProjectLocalMcpTab
                                setIsLoading={setIsLoading}
                                corporationDetail={corporationDetail}
                                projectInuseDetail={projectInuseDetail}
                                userDetail={userDetail}
                            />
                        )}
                    </div>
                );
            }
            return (
                <MCPServerList
                    setIsLoading={setIsLoading}
                    panelType={panelType}
                    listSettingApi={listSettingApi}
                    setListSettingApi={setListSettingApi}
                    setEditApiData={setEditApiData}
                    corporationDetail={corporationDetail}
                    projectInuseDetail={projectInuseDetail}
                    userDetail={userDetail}
                    props={props}
                    localMcpList={localMcpList}
                    setLocalMcpList={setLocalMcpList}
                />
            );
        }

        return (
            <EditMCPServer
                setIsLoading={setIsLoading}
                editApiData={editApiData}
                setEditApiData={setEditApiData}
                setListSettingApi={setListSettingApi}
                listSettingApi={listSettingApi}
                corporationDetail={corporationDetail}
                projectInuseDetail={projectInuseDetail}
                userDetail={userDetail}
                panelType={panelType}
                props={props}
                handleBackWithReload={handleBackWithReload}
            />
        );
    };

    return (
        <div className="mcp-server-wrapper">
            <LoadingPanel isLoading={isLoading} />
            {renderContent()}
        </div>
    );
}

export default MCPServerSetting;
